import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { triggerHaptic } from '@/hooks/useHaptics';
import { toast } from 'sonner';

interface Wave {
  id: string;
  sender_id: string;
  recipient_id: string;
  created_at: string;
}

const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours
const DAILY_WAVE_LIMIT = 15;

export function useWave() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch waves sent by current user
  const { data: sentWaves = [] } = useQuery({
    queryKey: ['waves', 'sent', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('waves')
        .select('*')
        .eq('sender_id', user.id);
      
      if (error) throw error;
      return (data || []) as Wave[];
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Count waves sent in last 24 hours
  const recentWaveCount = sentWaves.filter(w => {
    const waveTime = new Date(w.created_at).getTime();
    return Date.now() - waveTime <= COOLDOWN_MS;
  }).length;

  const hasReachedDailyLimit = recentWaveCount >= DAILY_WAVE_LIMIT;

  // Check if user can wave to a specific recipient (24h cooldown + daily limit)
  const canWave = (recipientId: string): boolean => {
    if (!user || user.id === recipientId) return false;
    if (hasReachedDailyLimit) return false;
    
    const existingWave = sentWaves.find(w => w.recipient_id === recipientId);
    if (!existingWave) return true;
    
    const waveTime = new Date(existingWave.created_at).getTime();
    const now = Date.now();
    return now - waveTime > COOLDOWN_MS;
  };

  // Check if user has waved recently (within 24h)
  const hasWavedRecently = (recipientId: string): boolean => {
    if (!user) return false;
    
    const existingWave = sentWaves.find(w => w.recipient_id === recipientId);
    if (!existingWave) return false;
    
    const waveTime = new Date(existingWave.created_at).getTime();
    const now = Date.now();
    return now - waveTime <= COOLDOWN_MS;
  };

  // Get time remaining until can wave again
  const getCooldownRemaining = (recipientId: string): number => {
    if (!user) return 0;
    
    const existingWave = sentWaves.find(w => w.recipient_id === recipientId);
    if (!existingWave) return 0;
    
    const waveTime = new Date(existingWave.created_at).getTime();
    const now = Date.now();
    const elapsed = now - waveTime;
    
    if (elapsed > COOLDOWN_MS) return 0;
    return COOLDOWN_MS - elapsed;
  };

  // Send wave mutation
  const sendWaveMutation = useMutation({
    mutationFn: async ({ recipientId, recipientName }: { recipientId: string; recipientName?: string }) => {
      if (!user) throw new Error('Not authenticated');
      if (user.id === recipientId) throw new Error("Can't wave at yourself");
      
      // Delete existing wave first (to reset cooldown)
      await supabase
        .from('waves')
        .delete()
        .eq('sender_id', user.id)
        .eq('recipient_id', recipientId);
      
      // Insert new wave
      const { error } = await supabase
        .from('waves')
        .insert({
          sender_id: user.id,
          recipient_id: recipientId,
        });
      
      if (error) throw error;
      return { recipientId, recipientName };
    },
    onSuccess: ({ recipientName }) => {
      triggerHaptic('success');
      // Haptic provides feedback
      queryClient.invalidateQueries({ queryKey: ['waves', 'sent', user?.id] });
    },
    onError: (error) => {
      console.error('Error sending wave:', error);
      const msg = error?.message || '';
      if (msg.includes('Daily wave limit')) {
        toast.error('You\'ve reached your daily wave limit. Try again tomorrow!');
      } else {
        toast.error('Failed to send wave');
      }
    },
  });

  return {
    sendWave: sendWaveMutation.mutate,
    isSending: sendWaveMutation.isPending,
    canWave,
    hasWavedRecently,
    getCooldownRemaining,
    hasReachedDailyLimit,
    recentWaveCount,
    dailyWaveLimit: DAILY_WAVE_LIMIT,
  };
}
